/**
 * ScopedCustomerLayout — layout for `/customer/:customerNumber/*` and
 * `/reseller/:resellerNumber/customer/:customerNumber/*` routes.
 *
 * Reads the customer (and optionally reseller) numbers from the URL,
 * looks them up via the API, and seeds the Zustand customer-context
 * (and reseller-context for the admin variant). Then renders the
 * matched child route via <Outlet />.
 *
 * Existing pages don't need to change — they continue using
 * `useCustomerContext()` / `useEffectiveCustomerId()`. Those hooks
 * now reflect whatever the URL params dictate.
 *
 * On bookmark / fresh load: the URL is the source of truth. On
 * internal navigation: this layout re-seeds context on every URL
 * change, so navigating between customers via URL works cleanly.
 *
 * Uses dedicated `by-number` endpoints (customers.getByNumber /
 * resellers.getByNumber) instead of paginating through the list, so
 * the layout doesn't break for resellers with > 50 customers (the
 * earlier list+find approach silently failed at page 2+).
 */
import { useEffect } from 'react';
import { Outlet, useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { customers as customersApi } from '@/api/customers';
import { resellers as resellersApi } from '@/api/resellers';
import { useCustomerContext } from '@/lib/customer-context-store';
import { useResellerContext } from '@/lib/reseller-context-store';
import { useAuth } from '@/lib/auth-store';
import { Loader2 } from 'lucide-react';

export function ScopedCustomerLayout() {
  const params = useParams<{ customerNumber?: string; resellerNumber?: string }>();
  const customerNumber = params.customerNumber != null ? Number(params.customerNumber) : null;
  const resellerNumber = params.resellerNumber != null ? Number(params.resellerNumber) : null;
  const setCustomer = useCustomerContext((s) => s.enter);
  const currentCustomer = useCustomerContext((s) => s.current);
  const setReseller = useResellerContext((s) => s.enter);
  const currentReseller = useResellerContext((s) => s.current);
  const user = useAuth((s) => s.user);
  const navigate = useNavigate();

  // Look up the reseller (if the URL has a reseller segment — super_admin
  // variant). Single round trip, cached by react-query.
  const resellerQ = useQuery({
    queryKey: ['reseller-by-number', resellerNumber],
    queryFn: async () => {
      if (resellerNumber == null) return null;
      try {
        return await resellersApi.getByNumber(resellerNumber);
      } catch (err) {
        // 404 is expected when the user can't see this reseller; treat
        // as "not found" rather than a hard error so the layout shows
        // the friendly fallback below.
        if ((err as { status?: number })?.status === 404) return null;
        throw err;
      }
    },
    enabled: resellerNumber != null,
  });

  const customerQ = useQuery({
    queryKey: ['customer-by-number', customerNumber, resellerNumber ?? 'any'],
    queryFn: async () => {
      if (customerNumber == null) return null;
      try {
        // The customers list is reseller-scoped for non-super-admins; for
        // super-admin we pass the reseller filter explicitly so the
        // returned customer is the right one (avoids cross-reseller
        // number collisions, where two customers could share #1001).
        return await customersApi.getByNumber(customerNumber, {
          resellerId: resellerQ.data?.id,
        });
      } catch (err) {
        if ((err as { status?: number })?.status === 404) return null;
        throw err;
      }
    },
    enabled: customerNumber != null && (!resellerNumber || !!resellerQ.data),
  });

  useEffect(() => {
    if (resellerQ.data) {
      setReseller({
        id: resellerQ.data.id,
        name: resellerQ.data.name,
        resellerNumber: resellerQ.data.reseller_number,
      });
    }
  }, [resellerQ.data, setReseller]);

  useEffect(() => {
    if (customerQ.data) {
      setCustomer({
        id: customerQ.data.id,
        name: customerQ.data.name,
        resellerId: customerQ.data.reseller_id,
        customerNumber: customerQ.data.customer_number,
      });
    }
  }, [customerQ.data, setCustomer]);

  // Loading state while we resolve URL → IDs. Quick — typically <100ms
  // since the by-number endpoint goes straight to the row and is cached
  // by react-query on subsequent loads.
  //
  // Also wait for the Zustand stores to actually reflect the URL before
  // we render <Outlet />. Without this gate, child routes wrapped in
  // <RequireCustomerContext> can race the useEffect that seeds the
  // store: on the render where customerQ.data resolves but useEffect
  // hasn't fired yet, the guard sees empty context and bounces to
  // /customers — a bad flash and a wrong destination.
  const customerReady =
    customerNumber == null ||
    (customerQ.data != null && currentCustomer?.id === customerQ.data.id);
  const resellerReady =
    resellerNumber == null ||
    (resellerQ.data != null && currentReseller?.id === resellerQ.data.id);
  const isLoading =
    (customerNumber != null && customerQ.isPending) ||
    (resellerNumber != null && resellerQ.isPending) ||
    (customerQ.data != null && !customerReady) ||
    (resellerQ.data != null && !resellerReady);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin me-2" />
        Loading…
      </div>
    );
  }

  // 404 if the URL points to a customer/reseller that doesn't exist or
  // the current user can't see (RLS hides cross-tenant rows). Push to
  // the closest valid list page.
  if (customerNumber != null && !customerQ.data) {
    return (
      <div className="p-6 space-y-3">
        <p className="text-muted-foreground">
          Customer #{customerNumber} not found or not accessible.
        </p>
        <button
          type="button"
          onClick={() => navigate(user?.is_super_admin ? '/customers' : '/dashboard')}
          className="text-sm text-blue-600 hover:underline"
        >
          Back to customers
        </button>
      </div>
    );
  }
  if (resellerNumber != null && !resellerQ.data) {
    return (
      <div className="p-6 space-y-3">
        <p className="text-muted-foreground">
          Reseller #{resellerNumber} not found or not accessible.
        </p>
        <button
          type="button"
          onClick={() => navigate('/resellers')}
          className="text-sm text-blue-600 hover:underline"
        >
          Back to resellers
        </button>
      </div>
    );
  }

  return <Outlet />;
}
